import { useMemo } from 'react';
import type { ComponentType } from 'react';
import {
  Camera,
  FolderInput,
  FolderMinus,
  FolderPlus,
  Mail,
  Rocket,
  Share2,
  Tag,
  Tags,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useArchiveAgent, useInterruptAgent, useUnarchiveAgent } from '@/hooks/agents/useAgents';
import { useApplyAgentMark } from '@/hooks/agents/useApplyAgentMark';
import { useApplyAgentUnread } from '@/hooks/agents/useApplyAgentUnread';
import { AGENT_MARK_CONFIG, agentStateChoices } from '@/lib/agent-marks';
import { isAgentStreamActive } from '@/lib/status-colors';
import { useRemoveAgentsFromGroup } from '@/hooks/agents/useAgentGroups';
import {
  agentGroupRef,
  DEVBOX_SNAPSHOTS_CAPABILITY,
  isSessionSharingDisabled,
} from '@reflex/shared';
import { useHasCapability } from '@/hooks/auth/useHasCapability';
import { useSessionSharingPolicy } from '@/hooks/auth/useSessionSharingPolicy';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgentAccess } from '@/hooks/agents/useAgentAccess';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { withOrgSlug } from '@/lib/routes';
import type { Agent, AgentActivity } from '@/lib/types';
import type { ResolvedAgentAction } from '@/lib/plugin-registry';
import { primaryAgentAction } from './agentLifecycleActions';
import { DeleteAgentDialog } from './DeleteAgentDialog';
import {
  LaunchAgainDialog,
  SaveAsPersonaDialog,
  SaveSnapshotDialog,
  ShareAgentDialog,
} from './lazyDialogs';
import { groupActionLabel } from '@/components/agent/groupActionLabel';
import { hotkeyKeys } from '@/lib/hotkeys';
import { AddLabelActionDialog, AddToGroupActionDialog } from './taxonomyActionDialogs';

type AgentActionIcon = ComponentType<{ className?: string }>;

/** Dialog component opened by a `dialog` action; manages its own form state. */
export type AgentActionDialogComponent = ComponentType<{
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>;

/**
 * Logical grouping used to order and visually separate actions in the
 * rail More menu. `primary` is the lifecycle CTA (Stop / Mark as /
 * Unarchive); the rest map to labeled menu sections.
 */
export type AgentActionGroup =
  | 'primary'
  | 'organize'
  | 'environment'
  | 'persistence'
  | 'execution'
  | 'danger';

const GROUP_ORDER: AgentActionGroup[] = [
  'primary',
  'organize',
  'environment',
  'persistence',
  'execution',
  'danger',
];

/**
 * Host actions that always live as icon buttons in the agent header (left of
 * the ⋮). Never pinned to the rail and excluded from Display → Actions.
 */
export const AGENT_HEADER_INLINE_ACTION_KEYS = [
  'stop',
  'unarchive',
  'state',
  'mark-unread',
  'share',
] as const;

export type AgentHeaderInlineActionKey = (typeof AGENT_HEADER_INLINE_ACTION_KEYS)[number];

export function isAgentHeaderInlineActionKey(key: string): key is AgentHeaderInlineActionKey {
  return (AGENT_HEADER_INLINE_ACTION_KEYS as readonly string[]).includes(key);
}

/**
 * Host actions that always live in the agent header ⋮ menu. They are never
 * pinned to the rail and are excluded from the Display → Actions pin list.
 */
export const AGENT_HEADER_OVERFLOW_ACTION_KEYS = [
  'launch-again',
  'save-as-persona',
  'delete',
] as const;

export type AgentHeaderOverflowActionKey = (typeof AGENT_HEADER_OVERFLOW_ACTION_KEYS)[number];

export function isAgentHeaderOverflowActionKey(key: string): key is AgentHeaderOverflowActionKey {
  return (AGENT_HEADER_OVERFLOW_ACTION_KEYS as readonly string[]).includes(key);
}

/** Header chrome (inline icons + ⋮ entries) — never a rail pin target. */
export function isAgentHeaderChromeActionKey(key: string): boolean {
  return isAgentHeaderInlineActionKey(key) || isAgentHeaderOverflowActionKey(key);
}

/** Fixed title-row plugin slot, immediately left of the tags control. */
export function isAgentHeaderBeforeLabelsAction(
  action: Pick<AgentActionBase, 'headerPlacement'>,
): boolean {
  return action.headerPlacement === 'before-labels';
}

/** Fixed right-side header plugin slot, immediately right of Share. */
export function isAgentHeaderInlinePluginAction(
  action: Pick<AgentActionBase, 'headerPlacement'>,
): boolean {
  return action.headerPlacement === 'header-inline';
}

/** Host header chrome plus plugin title-row / inline actions — never a rail pin. */
export function isAgentHeaderChromeAction(
  action: Pick<AgentActionBase, 'key' | 'headerPlacement'>,
): boolean {
  return (
    isAgentHeaderChromeActionKey(action.key) ||
    isAgentHeaderBeforeLabelsAction(action) ||
    isAgentHeaderInlinePluginAction(action)
  );
}

interface AgentActionBase {
  /** Stable identifier, also used for test ids and React keys. */
  key: string;
  label: string;
  icon: AgentActionIcon;
  /**
   * Color class for the icon. Lets an action carry the same color its subject
   * wears in the list, so picking a state and recognizing it later are the
   * same visual vocabulary.
   */
  iconClassName?: string;
  group: AgentActionGroup;
  destructive?: boolean;
  /** A mutation tied to this action is in flight. */
  pending?: boolean;
  /**
   * Anchors the interactive replay hint overlay (`data-replay-target`).
   * Only set on actions the replay engine can drive (complete / stop).
   */
  replayTarget?: 'complete' | 'stop';
  /**
   * Prefer this action as a one-click control on the agent rail (also
   * drives the default pin set). Ignored by the detail panel, which
   * renders every entry uniformly.
   */
  headerToolbar?: boolean;
  /**
   * Plugin actions that opt into a title-row slot. Host chrome keys do not
   * set this; they use {@link isAgentHeaderChromeActionKey}.
   */
  headerPlacement?: 'before-labels' | 'header-inline';
  /**
   * Extra search terms for flat searchable surfaces (the command palette),
   * covering vocabulary the label doesn't: the header calls labels "tags",
   * so "Add label" carries `tag`. Menus ignore these.
   */
  keywords?: string[];
  /**
   * Taught keyboard chord for this action (`hotkeyKeys(...)`), rendered as a
   * trailing hint in the overflow menu. Only set when a live binding exists.
   */
  shortcut?: readonly string[];
}

export interface AgentRunAction extends AgentActionBase {
  kind: 'run';
  onSelect: () => void;
}
export interface AgentDialogAction extends AgentActionBase {
  kind: 'dialog';
  Dialog: AgentActionDialogComponent;
}
export interface AgentLinkAction extends AgentActionBase {
  kind: 'link';
  to: string;
}

/** One choice inside an {@link AgentSubmenuAction}. */
export interface AgentSubmenuItem {
  key: string;
  label: string;
  /**
   * Self-contained label for flat surfaces (the command palette) where the
   * item renders without its parent trigger: `Blocked` under a "Mark as"
   * menu reads fine, but alone it needs to say `Mark as blocked`.
   */
  paletteLabel?: string;
  icon: AgentActionIcon;
  /** Color class for the icon; see {@link AgentActionBase.iconClassName}. */
  iconClassName?: string;
  onSelect: () => void;
  /** Shows a check next to the label — for the currently applied choice. */
  checked?: boolean;
}

/**
 * A group of mutually-related choices (the triage marks) rendered as one
 * affordance: a nested menu in the header dropdown, a dropdown button in the
 * detail panel. Keeps a family of options from flattening into N sibling
 * entries that would crowd out the run's lifecycle actions.
 */
export interface AgentSubmenuAction extends AgentActionBase {
  kind: 'submenu';
  items: AgentSubmenuItem[];
}

export type AgentAction = AgentRunAction | AgentDialogAction | AgentLinkAction | AgentSubmenuAction;

export interface UseAgentActionsOptions {
  /** Called after a successful archive (e.g. to refresh the sidebar). */
  onArchived?: () => void;
  /** Plugin-contributed agent actions to fold into the same list. */
  agentActions?: ResolvedAgentAction[];
  /**
   * The agent's derived activity, used to gate the Stop action. Stop only
   * makes sense while a turn is in flight (`working` / `starting`); a live
   * agent that is idle, awaiting input, or interrupted has no turn to
   * interrupt, so Stop is hidden for it. When omitted, the coarser
   * status-based liveness check ({@link isAgentActive}) is used instead.
   */
  activity?: AgentActivity;
  /**
   * Whether this surface binds the app's session hotkeys. Only the live agent
   * stream does (see `AgentStreamView`); an embedded or provided-context
   * replay renders the same menu with no listener behind it. Off by default so
   * a menu never teaches a chord that would do nothing where it is shown.
   */
  hotkeysBound?: boolean;
}

function resolvePluginHref(to: string, agentId: string, orgSlug: string): string {
  const filled = to.replace('{agentId}', agentId);
  if (/^https?:\/\//i.test(filled)) return filled;
  return withOrgSlug(orgSlug, filled);
}

/**
 * Single source of truth for the actions available on an agent — the same
 * set the stream header overflow, rail pins, and detail panel render from.
 * Handlers reuse the shared mutation hooks, which own their own toasts for
 * archive and unarchive; dialog actions reuse the existing launch / persona
 * dialogs.
 */
export function useAgentActions(agent: Agent, options: UseAgentActionsOptions = {}): AgentAction[] {
  const { onArchived, agentActions, activity, hotkeysBound = false } = options;
  const interrupt = useInterruptAgent();
  const archive = useArchiveAgent();
  const unarchive = useUnarchiveAgent();
  const removeFromGroup = useRemoveAgentsFromGroup();
  const applyMark = useApplyAgentMark();
  const markUnread = useApplyAgentUnread();
  const canSnapshot = useHasCapability(DEVBOX_SNAPSHOTS_CAPABILITY);
  // A `view` grant reads the run and nothing else. Everything below that
  // mutates it is left out rather than rendered into a guaranteed 403.
  const { canWrite } = useAgentAccess(agent);
  const { can } = useAuth();
  const orgSlug = useActiveOrgSlug();
  const sharingDisabled = isSessionSharingDisabled(useSessionSharingPolicy());

  return useMemo(() => {
    const entries: AgentAction[] = [];

    const isActive = isAgentStreamActive(agent);

    // Primary state action, a single slot that tracks the run's lifecycle:
    // Stop while it is working, then Archive once it has stopped working, then
    // Unarchive once archived. The decision, label, and icon come from the
    // shared `primaryAgentAction` so the header, detail panel, and sidebar row
    // never drift; only the handlers (direct mutations) live here.
    // Stop interrupts the agent's current turn but leaves the sandbox up (it
    // never deletes the agent); tearing the sandbox down happens only via
    // Delete.
    const primary = primaryAgentAction(agent, activity);
    if (!canWrite) {
      // The one state gesture that survives read-only access: unread is
      // per-viewer rather than a property of the run, so it rides on
      // `agents:read`. As the lone survivor it reads better as a plain
      // entry than as a one-item "Mark as" submenu.
      entries.push({
        kind: 'run',
        key: 'mark-unread',
        label: 'Mark as unread',
        icon: Mail,
        iconClassName: 'text-muted-foreground',
        group: 'primary',
        onSelect: () => markUnread(agent),
      });
    } else if (primary.kind === 'stop') {
      entries.push({
        kind: 'run',
        key: 'stop',
        label: primary.label,
        icon: primary.icon,
        group: 'primary',
        pending: interrupt.isPending,
        headerToolbar: true,
        replayTarget: 'stop',
        onSelect: () =>
          interrupt.mutate(agent.id, {
            onSuccess: () => toast.success('Agent stopped'),
            onError: (err: Error) =>
              toast.error('Failed to stop agent', { description: err.message }),
          }),
      });
    } else if (primary.kind === 'unarchive') {
      entries.push({
        kind: 'run',
        key: 'unarchive',
        label: primary.label,
        icon: primary.icon,
        group: 'primary',
        pending: unarchive.isPending,
        headerToolbar: true,
        // Toasts belong to `useUnarchiveAgent` (see the archive branch below).
        onSelect: () => unarchive.mutate(agent.id),
      });
    } else {
      // Settled and unarchived: the run's state is one question, so the primary
      // slot is the picker rather than a bare Archive button. `Archived` is a
      // peer state in that list alongside the marks, which is why there is no
      // separate `archive` entry — two controls for one decision is what made
      // the old menu read as a pile.
      entries.push({
        kind: 'submenu',
        key: 'state',
        label: agent.mark ? AGENT_MARK_CONFIG[agent.mark].label : 'Mark as',
        // Rail / toolbar affordance stays the Tag glyph; the applied mark's
        // icon and color live on the submenu choices, not the trigger.
        icon: Tag,
        iconClassName: agent.mark ? AGENT_MARK_CONFIG[agent.mark].className : undefined,
        group: 'primary',
        pending: archive.isPending,
        headerToolbar: true,
        // Ordering, labels, and colors come from the shared
        // `agentStateChoices` so this list and the sidebar row's menu can't
        // drift. Selecting the mark a run already carries clears it, so
        // applying and removing a mark are the same gesture. `Unread` rides
        // along in the same list: it is per-viewer rather than org-wide, so it
        // leaves the mark alone, but it is the same gesture from the user's
        // side.
        items: agentStateChoices(agent.mark).map((choice) => ({
          key: choice.key,
          label: choice.label,
          // "Clear mark" already carries its verb; the states get one.
          paletteLabel:
            choice.kind === 'clear' ? choice.label : `Mark as ${choice.label.toLowerCase()}`,
          icon: choice.icon,
          iconClassName: choice.iconClassName,
          checked: choice.checked,
          onSelect: () => {
            if (choice.kind === 'archive') {
              // The archived / failed toasts (and the Unarchive affordance on
              // the success one) belong to `useArchiveAgent`, so every archive
              // entry point confirms identically; only the local deselect
              // (`onArchived`, which closes the open run's panel) is this call
              // site's business.
              return archive.mutate(agent.id, { onSuccess: () => onArchived?.() });
            }
            if (choice.kind === 'unread') return markUnread(agent);
            return applyMark(agent, choice.checked ? null : choice.mark);
          },
        })),
      });
    }

    // Filing actions, same vocabulary as the sidebar row menu: which group
    // the run belongs to, and what kind of work it is. All three are
    // membership writes, so they need `agents:write` like the rest of the
    // mutations.
    if (canWrite) {
      // A run is filed in one group, so once it has one the picker moves it
      // rather than adding a second — the entry is named for what it does.
      const membership = agentGroupRef(agent);
      entries.push({
        kind: 'dialog',
        key: 'add-to-group',
        label: groupActionLabel([agent]),
        icon: membership ? FolderInput : FolderPlus,
        keywords: ['file', 'folder', 'organize', 'move'],
        group: 'organize',
        Dialog: AddToGroupActionDialog,
      });
      // Only offered when the run is actually in a group, so the action never
      // appears where it would be a no-op.
      if (membership) {
        entries.push({
          kind: 'run',
          key: 'remove-from-group',
          label: `Remove from ${membership.name}`,
          icon: FolderMinus,
          group: 'organize',
          onSelect: () => removeFromGroup.mutate({ groupId: membership.id, agentIds: [agent.id] }),
        });
      }
      entries.push({
        kind: 'dialog',
        key: 'add-label',
        label: 'Add label',
        icon: Tags,
        // The session header calls labels "tags"; carry that vocabulary so
        // ⌘K "tag" lands here.
        keywords: ['tag', 'tags'],
        group: 'organize',
        Dialog: AddLabelActionDialog,
      });
    }

    // Plugin-contributed actions (dialogs or links). They can be pinned to
    // the rail under their declared category (default: environment).
    for (const action of agentActions ?? []) {
      if (action.requiredPermission && !can(action.requiredPermission)) continue;
      if (action.requiresAgentWriteAccess && !canWrite) continue;
      const group = action.category ?? 'environment';
      if (action.component) {
        entries.push({
          kind: 'dialog',
          key: `plugin:${action.label}`,
          label: action.label,
          icon: action.icon,
          group,
          Dialog: action.component as AgentActionDialogComponent,
          ...(action.placement === 'header-before-labels'
            ? { headerPlacement: 'before-labels' as const }
            : action.placement === 'header-inline'
              ? { headerPlacement: 'header-inline' as const }
              : {}),
        });
      } else if (action.to) {
        entries.push({
          kind: 'link',
          key: `plugin:${action.label}`,
          label: action.label,
          icon: action.icon,
          group,
          to: resolvePluginHref(action.to, agent.id, orgSlug),
          ...(action.placement === 'header-before-labels'
            ? { headerPlacement: 'before-labels' as const }
            : action.placement === 'header-inline'
              ? { headerPlacement: 'header-inline' as const }
              : {}),
        });
      }
    }

    // An org that allows neither view nor edit shares has nothing the
    // dialog could offer, so the affordance goes away entirely rather than
    // opening onto a picker where every level is greyed out.
    if (!sharingDisabled) {
      entries.push({
        kind: 'dialog',
        key: 'share',
        label: 'Share',
        icon: Share2,
        group: 'persistence',
        Dialog: ShareAgentDialog,
      });
    }
    entries.push({
      kind: 'dialog',
      key: 'launch-again',
      label: 'Launch again',
      icon: Rocket,
      group: 'execution',
      Dialog: LaunchAgainDialog,
      ...(hotkeysBound ? { shortcut: hotkeyKeys('launchAgain') } : {}),
    });
    entries.push({
      kind: 'dialog',
      key: 'save-as-persona',
      label: 'Save as persona',
      icon: UserCircle,
      group: 'persistence',
      Dialog: SaveAsPersonaDialog,
    });

    // Snapshots need a live devbox disk to capture; the dialog itself
    // re-checks the real devbox status (a stream-active agent can still be
    // sitting on an auto-suspended box, which is fine to snapshot). Gated by
    // the org's `devbox-snapshots` entitlement — hidden entirely without it.
    if (agent.devboxId && isActive && canSnapshot && canWrite) {
      entries.push({
        kind: 'dialog',
        key: 'save-snapshot',
        label: 'Save snapshot',
        icon: Camera,
        group: 'persistence',
        Dialog: SaveSnapshotDialog,
      });
    }

    // Delete is the one irreversible action here — it removes the run and tears
    // its sandbox down — so it goes through a confirm dialog rather than firing
    // on a single click. The mutation lives in the dialog.
    if (canWrite) {
      entries.push({
        kind: 'dialog',
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        group: 'danger',
        destructive: true,
        Dialog: DeleteAgentDialog,
      });
    }

    return entries.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
  }, [
    agent,
    activity,
    onArchived,
    agentActions,
    interrupt,
    archive,
    unarchive,
    removeFromGroup,
    applyMark,
    markUnread,
    canSnapshot,
    can,
    canWrite,
    orgSlug,
    sharingDisabled,
    hotkeysBound,
  ]);
}
